// Admin org settings — nonprofit credentials + public contact emails.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Inbox } from "lucide-react";
import { getOrgSettings, adminUpdateOrgSettings, adminContactMessages, errText } from "@/lib/api";
import { invalidateOrgSettings } from "@/lib/org";
import { inputCls, goldBtnCls, fmtDate } from "@/components/admin/constants";

const FIELDS = [
  { key: "org_name", label: "Organization name" },
  { key: "org_tagline", label: "Tagline (footer)" },
  { key: "nonprofit_designation", label: "Nonprofit designation" },
  { key: "ein", label: "EIN (leave blank to hide)" },
  { key: "founding_location", label: "Founding cohort location" },
  { key: "email_general", label: "General contact email" },
  { key: "email_partnerships", label: "Partnerships email" },
  { key: "email_cohort", label: "Cohort email" },
];

export const ContactInbox = () => {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => { adminContactMessages().then(setMsgs).catch(() => setMsgs([])); }, []);

  return (
    <div className="mt-6 max-w-3xl border border-silver/20 bg-charcoal/60 p-6" data-testid="contact-inbox">
      <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">
        <Inbox size={11} className="mr-1.5 inline align-middle" /> Contact inbox
      </p>
      {!msgs ? (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-silver/50">Loading messages...</p>
      ) : msgs.length === 0 ? (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-silver/50" data-testid="contact-inbox-empty">No contact messages yet.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {msgs.map((m) => (
            <li key={m.id} className="border border-silver/15 bg-onyx p-4" data-testid={`contact-msg-${m.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-head text-sm font-extrabold uppercase tracking-wide text-cream">{m.name}</p>
                <span className="border border-gold/40 px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-goldlight">{m.category}</span>
              </div>
              <p className="mt-1 font-mono text-[10px] text-silver/60">
                <a href={`mailto:${m.email}`} className="text-goldlight/80 hover:text-goldlight">{m.email}</a> · {fmtDate(m.created_at)} · {m.id}
              </p>
              <p className="mt-2 whitespace-pre-wrap font-body text-sm leading-relaxed text-silver/85">{m.message}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const OrgSettingsPanel = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getOrgSettings().then((d) => setForm((p) => p || d)).catch((e) => toast.error(errText(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await adminUpdateOrgSettings(form);
      setForm(updated);
      invalidateOrgSettings();
      toast.success("Organization settings saved.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50">Loading settings...</p>;

  return (
    <form onSubmit={save} className="mt-6 max-w-3xl border border-gold/25 bg-charcoal p-6" data-testid="org-settings-panel">
      <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Organization credentials</p>
      <p className="mt-2 font-body text-sm text-silver/70">
        These values appear in the footer, About page, and Contact page. Changes go live immediately — no code edits needed.
      </p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="mb-1.5 block font-mono text-[9px] uppercase tracking-[0.25em] text-silver/60">{f.label}</span>
            <input
              value={form[f.key] || ""}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              data-testid={`org-${f.key}-input`}
              className={inputCls + " w-full"}
            />
          </label>
        ))}
      </div>
      <button type="submit" disabled={saving} data-testid="org-settings-save-btn" className={goldBtnCls + " mt-6 inline-flex items-center gap-2 px-6 py-3 text-xs"}>
        <Save size={13} /> {saving ? "Saving..." : "Save Settings"}
      </button>
    </form>
  );
};
